/**
 * SettingsFilterContext - Settings-specific filter state management
 *
 * This context manages filter state for settings pages (Users, Groups, Units)
 * independently from the Map page's FilterContext.
 *
 * Key differences from FilterContext:
 * - Separate localStorage persistence (won't affect Map filters)
 * - Industry-based filtering (Industry Type → Company industry)
 * - Simplified API (no unit data, no cache restoration)
 *
 * The Provider component lives in ./SettingsFilterProvider (react-refresh
 * only-export-components: the hook + context + constants stay here).
 */

import { createContext, useContext } from 'react';
import type { IndustryType } from '@/types';

// Available industries (matches IndustryType)
export const AVAILABLE_INDUSTRIES: IndustryType[] = ['film', 'event', 'gov', 'utility', 'relief'];

export interface SettingsFilterCompany {
  id: string;
  name: string;
  slug: string;
  industry?: string;
  is_internal: boolean;
}

export interface SettingsFilterGroup {
  id: string;
  name: string;
  company_id: string;
  company_name?: string;
}

export interface SettingsFilterContextType {
  // Selection state
  selectedIndustries: IndustryType[];
  selectedCompanyIds: string[];
  selectedGroupIds: string[];

  // Reference data (from RBAC)
  companies: SettingsFilterCompany[];
  groups: SettingsFilterGroup[];

  // Derived - filtered by selections
  filteredCompanies: SettingsFilterCompany[];
  filteredGroups: SettingsFilterGroup[];

  // Setters
  setSelectedIndustries: (industries: IndustryType[]) => void;
  setSelectedCompanyIds: (ids: string[]) => void;
  setSelectedGroupIds: (ids: string[]) => void;

  // Utilities
  clearAllFilters: () => void;
  isLoading: boolean;

  // Helper to check if any filters are active
  hasActiveFilters: boolean;

  // Ensure a specific industry is selected (for post-create filter adjustment)
  ensureIndustrySelected: (industry: IndustryType) => void;

  // Ensure a specific company is selected (for post-create filter adjustment)
  ensureCompanySelected: (companyId: string) => void;
}

export const SettingsFilterContext = createContext<SettingsFilterContextType | undefined>(undefined);

export function useSettingsFilters() {
  const context = useContext(SettingsFilterContext);
  if (!context) {
    throw new Error('useSettingsFilters must be used within SettingsFilterProvider');
  }
  return context;
}
